import React from 'react';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Warning } from '@phosphor-icons/react';
import { FILE_TYPE_LABEL, formatDateTime } from './constants';
import FixFieldButton from './FixFieldButton';
import RawEdiViewer from './RawEdiViewer';

const computeStatus = (ftype, r) => {
  const isErrorReportReject =
    ftype === 'ErrorReport' && (r.parsed?.overall_status || r.overall_status) === 'REJECT';
  const is824Reject = ftype === '824' && (r.totals?.rejected || 0) > 0;
  const isReject =
    (r.status || '').toLowerCase().startsWith('reject') || isErrorReportReject || is824Reject;

  let statusLabel = r.status || 'Processed';
  if (ftype === 'ErrorReport') {
    const overall = r.parsed?.overall_status;
    statusLabel = overall === 'REJECT' ? 'Rejected' : overall === 'PARTIAL' ? 'Partial' : 'Accepted';
  } else if (ftype === '824') {
    statusLabel = is824Reject ? 'Rejected' : 'Accepted';
  }
  return { isReject, statusLabel };
};

const ElementErrorRow = ({ e }) => (
  <div className="text-xs bg-white/60 border border-red-200 rounded px-2 py-1.5">
    <div className="font-semibold text-red-800">
      {e.error_description || e.error_code_label || 'Validation error'}
    </div>
    <div className="text-red-700 mt-0.5">
      <span className="font-mono">{e.segment}</span>
      {e.loop ? <span className="text-red-500"> · loop {e.loop}</span> : null}
      {e.element ? <span className="text-red-500"> · {e.element}</span> : null}
      {e.data_content ? (
        <>
          <span className="text-red-500"> · value </span>
          <span className="font-mono bg-red-100 px-1 rounded">{e.data_content}</span>
        </>
      ) : null}
    </div>
    <FixFieldButton fix={e.fix} />
  </div>
);

const TedErrorRow = ({ e }) => (
  <div className="text-xs bg-white/60 border border-red-200 rounded px-2 py-1.5">
    <div className="text-red-700">
      <span className="font-mono">{e.segment}</span>
      <span className="text-red-500"> · pos {e.segment_position}</span>
      <span className="text-red-500"> · element {e.element_position}</span>
      {e.data_content ? (
        <>
          <span className="text-red-500"> = </span>
          <span className="font-mono bg-red-100 px-1 rounded">{e.data_content}</span>
        </>
      ) : null}
      {e.red_code && (
        <span className="ml-2 text-red-600 font-semibold">{e.red_code}</span>
      )}
    </div>
    <FixFieldButton fix={e.fix} />
  </div>
);

const ResponseEntry = ({ ftype, r }) => {
  const { isReject, statusLabel } = computeStatus(ftype, r);
  const elementErrors = ftype === 'ErrorReport' ? r.parsed?.errors || r.errors || [] : [];
  const rejectionReasons = ftype === 'ErrorReport' ? r.parsed?.rejection_reasons || [] : [];
  const ted = ftype === '824' ? r.errors || [] : [];

  return (
    <div
      className={`border rounded-lg p-3 ${
        isReject ? 'border-red-200 bg-red-50/40' : 'border-emerald-200 bg-emerald-50/30'
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <Badge
          className={`text-[10px] ${
            isReject ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
          }`}
          data-testid={`odm-response-${ftype}-status-badge`}
        >
          {statusLabel}
        </Badge>
        <span className="text-xs text-slate-500">{formatDateTime(r.processed_at)}</span>
      </div>
      <p className="font-mono text-[11px] text-slate-600 truncate" title={r.filename}>
        {r.filename}
      </p>
      {r.parsed?.note_message && (
        <p className="text-xs text-slate-700 mt-2">
          <Warning size={12} className="inline mr-1 text-amber-600" weight="fill" />
          Note {r.parsed.note_code}: {r.parsed.note_message}
        </p>
      )}
      {typeof r.summary === 'string' && r.summary && (
        <p className="text-xs text-slate-600 mt-1">{r.summary}</p>
      )}
      {rejectionReasons.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid="odm-error-report-reasons">
          {rejectionReasons.map((rr) => (
            <li
              key={`type-${rr.type}`}
              className="text-xs text-red-800 bg-white/60 border border-red-200 rounded px-2 py-1"
            >
              <span className="font-semibold">TYPE {rr.type}:</span> {rr.reason}
            </li>
          ))}
        </ul>
      )}
      {elementErrors.length > 0 && (
        <div className="mt-2 space-y-1" data-testid="odm-error-report-errors">
          {elementErrors.map((e, idx) => (
            <ElementErrorRow key={`er-${idx}-${e.element || ''}-${e.position || ''}`} e={e} />
          ))}
        </div>
      )}
      {ftype === '824' && r.totals && (
        <div className="mt-2 flex gap-2 text-xs" data-testid="odm-824-totals">
          <span className="bg-emerald-100 text-emerald-800 rounded px-2 py-0.5">
            {r.totals.accepted || 0} accepted
          </span>
          <span className="bg-red-100 text-red-800 rounded px-2 py-0.5">
            {r.totals.rejected || 0} rejected
          </span>
          {r.originator_reference && (
            <span className="bg-slate-100 text-slate-700 rounded px-2 py-0.5 font-mono">
              ref: {r.originator_reference}
            </span>
          )}
        </div>
      )}
      {ted.length > 0 && (
        <div className="mt-2 space-y-1" data-testid="odm-824-errors">
          {ted.map((e, idx) => (
            <TedErrorRow
              key={`ted-${idx}-${e.segment || ''}-${e.segment_position || ''}`}
              e={e}
            />
          ))}
        </div>
      )}
      <RawEdiViewer raw={r.raw_content} filename={r.filename} />
    </div>
  );
};

const ResponseCard = ({ ftype, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <Card data-testid={`odm-response-${ftype}-card`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{FILE_TYPE_LABEL[ftype] || ftype}</CardTitle>
        <CardDescription className="text-xs">
          {items.length} response{items.length === 1 ? '' : 's'} from ODM
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.map((r) => (
          <ResponseEntry key={r.filename} ftype={ftype} r={r} />
        ))}
      </CardContent>
    </Card>
  );
};

export default ResponseCard;
